import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { SlotOpsTimeRestriction, User } from '../Modules/User/Model/User';
import { isLimitedPartnerAdmin, isSlotOpsOverrideActive } from './limitedPartnerAdmin';

dayjs.extend(utc);
dayjs.extend(timezone);

const KST_TIMEZONE = 'Asia/Seoul';
const SLOT_PERMISSION_DEADLINE = dayjs.tz('2026-03-02 12:00', KST_TIMEZONE);
const SLOT_PERMISSION_RESTRICTION_DATE_START = dayjs.tz('2026-03-02', KST_TIMEZONE).startOf('day');
const SLOT_PERMISSION_RESTRICTION_DATE_END = dayjs.tz('2026-03-02', KST_TIMEZONE).endOf('day');
const LIMITED_ADMIN_FULL_RESTRICTION_DATE = '2026-03-02';
const MAY_ORDER_TIME_RESTRICTION_DATES = new Set([
  '2026-05-01',
  '2026-05-02',
  '2026-05-03',
  '2026-05-05',
]);
const EXTENDED_WEEKDAY_RESTRICTION_DATE = '2026-05-11';
const EXTENDED_WEEKDAY_RESTRICTION_USERNAME = 'tkwkd';
const WEEKEND_LIKE_RESTRICTION_DATES = new Set([
  '2026-05-25',
  '2026-06-03',
  '2026-07-17', // 주말과 동일하게 12:00 이후 제한 (일반 ADMIN 제외, 제한형 ADMIN 포함)
]);
const LIMITED_ADMIN_WEEKDAY_START = 17 * 60 + 30; // 17:30
const LIMITED_ADMIN_WEEKEND_START = 15 * 60; // 15:00
const WEEKEND_LIKE_RESTRICTION_START = 12 * 60; // 12:00

export type TimeRestrictionUser = Pick<
  User,
  | 'grade'
  | 'username'
  | 'is_limited_partner_admin'
  | 'external_partner_flags'
  | 'slot_ops_override'
  | 'slot_ops_time_restriction'
>;

function formatCutoffLabel(minutes?: number, fallback?: string): string {
  if (minutes == null || Number.isNaN(minutes)) return fallback || '--:--';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

function isRestrictedByServerPolicy(
  policy: SlotOpsTimeRestriction,
  nowKST: dayjs.Dayjs,
): boolean {
  if (!policy.applies) return false;
  const currentMinutes = nowKST.hour() * 60 + nowKST.minute();
  const today = nowKST.format('YYYY-MM-DD');
  if (policy.today_date === today && policy.today_cutoff_minutes != null) {
    return currentMinutes >= policy.today_cutoff_minutes;
  }
  const serverDay = (nowKST.day() + 6) % 7; // Python weekday: 월=0 ... 일=6
  if (policy.daily_cutoff_minutes?.length === 7) {
    return currentMinutes >= policy.daily_cutoff_minutes[serverDay];
  }
  const isWeekend = nowKST.day() === 0 || nowKST.day() === 6;
  const cutoff = isWeekend
    ? policy.weekend_cutoff_minutes ?? 12 * 60
    : policy.weekday_cutoff_minutes ?? 17 * 60;
  return currentMinutes >= cutoff;
}

function normalizedUsername(username?: string): string {
  return username?.trim().toLowerCase() ?? '';
}

function evaluateTimeRestriction(
  nowKST: dayjs.Dayjs,
  username: string | undefined,
  isLimitedAdmin: boolean,
): boolean {
  const dateStr = nowKST.format('YYYY-MM-DD');
  const dayOfWeek = nowKST.day();
  const currentTime = nowKST.hour() * 60 + nowKST.minute();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  // 주말형 특별일: 일반 ADMIN 제외 전원(제한형 ADMIN 포함) 12:00 이후 제한
  if (WEEKEND_LIKE_RESTRICTION_DATES.has(dateStr)) {
    return currentTime >= WEEKEND_LIKE_RESTRICTION_START;
  }

  if (isLimitedAdmin) {
    if (dateStr === LIMITED_ADMIN_FULL_RESTRICTION_DATE) {
      return true;
    }
    if (isWeekend) {
      return currentTime >= LIMITED_ADMIN_WEEKEND_START;
    }
    return currentTime >= LIMITED_ADMIN_WEEKDAY_START;
  }

  const isDeadlineRestrictionDate =
    nowKST.valueOf() >= SLOT_PERMISSION_RESTRICTION_DATE_START.valueOf() &&
    nowKST.valueOf() <= SLOT_PERMISSION_RESTRICTION_DATE_END.valueOf();

  if (!isLimitedAdmin && isDeadlineRestrictionDate && nowKST.valueOf() > SLOT_PERMISSION_DEADLINE.valueOf()) {
    return true;
  }

  const specialStart = dayjs.tz('2026-02-14', KST_TIMEZONE).startOf('day');
  const specialEnd = dayjs.tz('2026-02-18', KST_TIMEZONE).endOf('day');
  const isSpecialPeriod =
    nowKST.valueOf() >= specialStart.valueOf() &&
    nowKST.valueOf() <= specialEnd.valueOf();

  const weekendRestrictionStart = 12 * 60;
  const mayOrderRestrictionStart = 13 * 60;
  const extendedWeekdayRestrictionStart = 19 * 60;
  const weekdayRestrictionStart = isSpecialPeriod ? 12 * 60 : 17 * 60;
  const uname = normalizedUsername(username);

  if (isSpecialPeriod) {
    return currentTime >= weekendRestrictionStart;
  }

  if (MAY_ORDER_TIME_RESTRICTION_DATES.has(dateStr)) {
    return currentTime >= mayOrderRestrictionStart;
  }

  if (
    dateStr === EXTENDED_WEEKDAY_RESTRICTION_DATE &&
    uname === EXTENDED_WEEKDAY_RESTRICTION_USERNAME
  ) {
    return currentTime >= extendedWeekdayRestrictionStart;
  }

  if (isWeekend) {
    return currentTime >= weekendRestrictionStart;
  }

  return currentTime >= weekdayRestrictionStart;
}

/**
 * 제한형 관리자 기준 현재 시각이 슬롯 작업 제한 시간대인지 (임시 해제 여부 무관)
 */
export function isLimitedAdminInRestrictedHours(
  user: TimeRestrictionUser | null | undefined,
): boolean {
  if (!user || !isLimitedPartnerAdmin(user)) return false;
  const nowKST = dayjs().tz(KST_TIMEZONE);
  if (user.slot_ops_time_restriction) {
    return isRestrictedByServerPolicy(
      { ...user.slot_ops_time_restriction, applies: true },
      nowKST,
    );
  }
  return evaluateTimeRestriction(nowKST, user.username, true);
}

/**
 * 현재 시간이 슬롯 수정/삭제/생성이 제한되는 시간인지 확인
 */
export function isSlotOperationRestricted(user: TimeRestrictionUser | null | undefined): boolean {
  if (!user) return false;

  if (user.grade === 'ADMIN' && !isLimitedPartnerAdmin(user)) {
    return false;
  }

  if (isLimitedPartnerAdmin(user) && isSlotOpsOverrideActive(user)) {
    return false;
  }

  const nowKST = dayjs().tz(KST_TIMEZONE);
  if (user.slot_ops_time_restriction) {
    return isRestrictedByServerPolicy(user.slot_ops_time_restriction, nowKST);
  }
  return evaluateTimeRestriction(nowKST, user.username, isLimitedPartnerAdmin(user));
}

/**
 * 제한 시간에 대한 설명 메시지 반환
 */
export function getRestrictionMessage(
  action: '수정' | '생성' | '연장' | '삭제' | '환불' = '수정',
  user?: TimeRestrictionUser | null,
): string {
  const nowKST = dayjs().tz(KST_TIMEZONE);
  const dateStr = nowKST.format('YYYY-MM-DD');
  const policy = user?.slot_ops_time_restriction;
  if (policy) {
    if (!policy.applies) {
      return `관리자는 슬롯 ${action} 시간 제한이 없습니다.`;
    }
    if (policy.message) {
      return policy.message.replace(/작업/g, action);
    }
    return `한국 시간 기준 평일 ${formatCutoffLabel(policy.weekday_cutoff_minutes, policy.weekday_cutoff)} 이후 또는 주말 ${formatCutoffLabel(policy.weekend_cutoff_minutes, policy.weekend_cutoff)} 이후에는 슬롯 ${action}이 제한됩니다.`;
  }
  const isLimitedAdmin = isLimitedPartnerAdmin(user ?? null);
  const uname = normalizedUsername(user?.username);

  if (isLimitedAdmin && dateStr === LIMITED_ADMIN_FULL_RESTRICTION_DATE) {
    return `2026-03-02 당일에는 제한형 관리자의 슬롯 ${action}이 제한됩니다.`;
  }

  const isDeadlineRestrictionDate =
    nowKST.valueOf() >= SLOT_PERMISSION_RESTRICTION_DATE_START.valueOf() &&
    nowKST.valueOf() <= SLOT_PERMISSION_RESTRICTION_DATE_END.valueOf();

  if (!isLimitedAdmin && isDeadlineRestrictionDate && nowKST.valueOf() > SLOT_PERMISSION_DEADLINE.valueOf()) {
    return `2026-03-02 당일 12:00 이후에는 슬롯 ${action}이 제한됩니다.`;
  }

  const specialStart = dayjs.tz('2026-02-14', KST_TIMEZONE).startOf('day');
  const specialEnd = dayjs.tz('2026-02-18', KST_TIMEZONE).endOf('day');
  const isSpecialPeriod =
    nowKST.valueOf() >= specialStart.valueOf() &&
    nowKST.valueOf() <= specialEnd.valueOf();

  if (isSpecialPeriod) {
    return `2026-02-14 ~ 2026-02-18 기간에는 12:00 이후 슬롯 ${action}이 제한됩니다.`;
  }

  if (MAY_ORDER_TIME_RESTRICTION_DATES.has(dateStr)) {
    return `2026-05-01, 05-02, 05-03, 05-05에는 13:00 이후 슬롯 ${action}이 제한됩니다.`;
  }

  if (dateStr === EXTENDED_WEEKDAY_RESTRICTION_DATE && uname === EXTENDED_WEEKDAY_RESTRICTION_USERNAME) {
    return `2026-05-11 당일에는 19:00 이후 슬롯 ${action}이 제한됩니다.`;
  }

  if (WEEKEND_LIKE_RESTRICTION_DATES.has(dateStr)) {
    return `${dateStr} 당일(KST)에는 주말과 동일하게 12:00 이후 슬롯 ${action}이 제한됩니다.`;
  }

  if (isLimitedAdmin) {
    return `한국 시간 기준 제한형 관리자는 평일 17:30 이후 또는 주말 15:00 이후 슬롯 ${action}이 제한됩니다. 슬롯 페이지 상단에서 15분 임시 해제를 이용할 수 있습니다.`;
  }

  return `주말 12:00 이후 또는 평일 17:00 이후에는 슬롯 ${action}이 제한됩니다.`;
}
